import React, { useMemo } from 'react';
import { Box, TextField, TextFieldProps } from '@mui/material';

interface INumberFieldComponentProps
  extends Omit<TextFieldProps, 'InputProps' | 'InputLabelProps' | 'onChange'> {
  label: string;
  value: number | null;
  errorMessage?: string;
  onValueChange: (value: number | null) => void;
  isMonetary?: boolean;
  isPercentage?: boolean;
  isOptional?: boolean;
  decimalPrecision?: number;
  mb?: string | number;
  borderColor?: string;
  textColor?: string;
  labelColor?: string;
  isDisabled?: boolean;
  minValue?: number;
  maxValue?: number;
  size?: 'small' | 'medium';
  isFullWidth?: boolean;
  valueAlign?: 'left' | 'center' | 'right';
  onValueBlur?: () => void;
}

export const NumberFieldComponent = ({
  label,
  value,
  onValueChange,
  errorMessage,
  isMonetary = false,
  isPercentage = false,
  isOptional = false,
  decimalPrecision = 0,
  mb = 3,
  borderColor,
  textColor,
  labelColor,
  isDisabled = false,
  isFullWidth = true,
  minValue,
  maxValue,
  valueAlign = 'left',
  size = 'medium',
  onValueBlur,
  ...props
}: INumberFieldComponentProps) => {
  const inputField = isOptional ? `${label} (Opcional)` : label;

  const formattedValue = useMemo(() => {
    if (value === null || isNaN(value)) return '';

    const options: Intl.NumberFormatOptions = {
      minimumFractionDigits: decimalPrecision,
      maximumFractionDigits: decimalPrecision,
    };

    if (isMonetary) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        ...options,
      }).format(value);
    }

    if (isPercentage) {
      return `${new Intl.NumberFormat('pt-BR', options).format(value)}%`;
    }

    return new Intl.NumberFormat('pt-BR', options).format(value);
  }, [value, isMonetary, isPercentage, decimalPrecision]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const numericString = event.target.value.replace(/\D/g, '');

    if (numericString === '') {
      onValueChange(null);
      return;
    }

    const factor = Math.pow(10, decimalPrecision);
    let numericValue = parseInt(numericString, 10) / factor;

    if (maxValue && numericValue > maxValue) {
      numericValue = maxValue;
    }

    onValueChange(numericValue);
  };

  const handleBlur = () => {
    if (
      minValue !== undefined &&
      ((value !== null && value !== undefined && value < minValue) ||
        value === null)
    ) {
      onValueChange(minValue);
    }

    if (onValueBlur) onValueBlur();
  };

  return (
    <Box sx={{ mb: mb, width: '100%', display: 'flex' }}>
      <TextField
        {...props}
        fullWidth={isFullWidth}
        size={size}
        label={inputField}
        value={formattedValue}
        onChange={handleChange}
        onBlur={handleBlur}
        error={!!errorMessage}
        helperText={errorMessage}
        disabled={isDisabled}
        slotProps={{
          htmlInput: {
            style: { textAlign: valueAlign },
            inputMode: 'numeric',
          },
        }}
        sx={{ ...props.sx }}
      />
    </Box>
  );
};
